// Bausteine der Seite Turnier-Einstellungen: Symbole, Felder, Gruppenliste,
// Bearbeiten, Seitenleiste. Gestaltung in styles/turniereinstellungen.css (te-)
// auf den Grundbausteinen aus styles/vereine.css (vv-).
import React, { useEffect, useRef, useState } from 'react';
import AusschreibungVorlagen from '../../components/turnier/AusschreibungVorlagen';
import { zeileVon, zeitplanFelder, zusatzFuer, ausgeblendetWertung } from '../../lib/einstellungenWirkung';
import { ALT_ZEILEN, dauerSekunden, dauerText, minutenZumSpeichern } from '../../lib/kampfRegeln';
import {
  BASIS_ID, STATUS, PUNKT, VORLAGEN, titelVon, status, statusWort, kurzWert, zpWert, wertungFelder,
  geaenderteFelder, rechnungText, basisSchalter,
} from './einstellungenModell';

const PFADE = {
  rechts: <path d="m9 18 6-6-6-6" />,
  links: <path d="m15 18-6-6 6-6" />,
  achtung: <><path d="M12 3 2 20h20L12 3z" /><path d="M12 10v4M12 17h.01" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
  uhr: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  haken: <path d="M20 6 9 17l-5-5" />,
  schloss: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  pfeil: <path d="M5 12h14M13 6l6 6-6 6" />,
  zurueck: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></>,
  wolke: <><path d="M7 18a5 5 0 1 1 .9-9.9A6 6 0 0 1 19 10a4 4 0 0 1-1 8H7z" /><path d="m10 13 4 4M14 13l-4 4" /></>,
  leute: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14a6 6 0 0 1 3.5 6" /></>,
  rechner: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M8 7h8M8 11h2M12 11h2M8 15h2M12 15h2M16 11v4" /></>,
  dok: <><path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>,
  matte: <><rect x="3" y="6" width="18" height="12" rx="1.5" /><circle cx="12" cy="12" r="2.5" /></>,
  baum: <path d="M4 5h5v4H4zM4 15h5v4H4zM15 10h5v4h-5zM9 7h3v10H9M12 12h3" />,
};
export function Ico({ n }) {
  return <svg className="vv-svg" viewBox="0 0 24 24" aria-hidden="true">{PFADE[n]}</svg>;
}

export function StatusMarke({ id, d, ctx }) {
  const s = status(id, d, ctx);
  return <span className={`vv-st ${STATUS[s][0]}`}>{statusWort(id, s, d, ctx)}</span>;
}

/* ═══ Eingabefeld ═══ */
// Dauer (25.10.2026): Text statt Zahl, damit „1:30“, „90 s“ oder „1,5“ geht.
// Gespeichert wird in Minuten mit Bruchteil (lib/kampfRegeln.js), bei Unsinn
// der Text selbst (dann Fehler am Feld). Während der Eingabe bleibt der Text
// stehen; beim Verlassen wird er als „1:30“ bzw. „2“ geschrieben.
const dauerAnzeige = (w) => (typeof w === 'number' ? (Number.isFinite(w) ? dauerText(Math.round(w * 60)) : '') : (w ?? ''));
function DauerEingabe({ domId, fid, wert, onWert, nurLesen, fehler }) {
  const [text, setText] = useState(() => dauerAnzeige(wert));
  const fokus = useRef(false);
  useEffect(() => { if (!fokus.current) setText(dauerAnzeige(wert)); }, [wert]);
  return (
    <input id={domId} data-feld={fid} type="text" inputMode="text" autoComplete="off"
      value={text} readOnly={nurLesen} aria-invalid={!!fehler} placeholder="z. B. 1:30"
      onFocus={() => { fokus.current = true; }}
      onBlur={() => { fokus.current = false; setText(dauerAnzeige(wert)); }}
      onChange={(e) => {
        const v = e.target.value;
        setText(v);
        if (v.trim() === '') { onWert(''); return; }
        const sek = dauerSekunden(v);
        onWert(sek == null ? v : minutenZumSpeichern(sek));
      }} />
  );
}

export function ZahlFeld({ fid, label, wert, onWert, einheit, min, max, typ, fehler, neu, erbt, nurLesen, tipp }) {
  const domId = `te-f-${fid.replace(/[^a-zA-Z0-9]/g, '-')}`;
  if (typ === 'dauer') {
    return (
      <div className={`vv-feld${fehler ? ' vv-ist-fehler' : ''}${neu ? ' te-ist-neu' : ''}${erbt ? ' te-erbt' : ''}`}>
        <label htmlFor={domId}>{neu && <span className="te-neu" title="geändert" />}{label}</label>
        <div className="te-eing">
          <DauerEingabe domId={domId} fid={fid} wert={wert} onWert={onWert} nurLesen={nurLesen} fehler={fehler} />
          {einheit && <span className="te-einh">{einheit}</span>}
        </div>
        {fehler
          ? <span className="vv-fehl" role="alert"><Ico n="achtung" />{fehler}</span>
          : tipp ? <span className="vv-tipp">{tipp}</span> : null}
      </div>
    );
  }
  if (typ === 'schalter') {
    return (
      <div className={`vv-feld${neu ? ' te-ist-neu' : ''}`}>
        <span className="te-feld-l" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--vv-text-2)' }}>{neu && <span className="te-neu" />}{label}</span>
        <label className="te-schalter"><input type="checkbox" checked={!!wert} disabled={nurLesen} onChange={(e) => onWert(e.target.checked)} />{wert ? 'an' : 'aus'}</label>
        {tipp && <span className="vv-tipp">{tipp}</span>}
      </div>
    );
  }
  return (
    <div className={`vv-feld${fehler ? ' vv-ist-fehler' : ''}${neu ? ' te-ist-neu' : ''}${erbt ? ' te-erbt' : ''}`}>
      <label htmlFor={domId}>{neu && <span className="te-neu" title="geändert" />}{label}</label>
      <div className="te-eing">
        <input id={domId} data-feld={fid}
          type={typ === 'zeit' ? 'time' : typ === 'text' ? 'text' : 'number'}
          inputMode={typ === 'zeit' || typ === 'text' ? undefined : 'numeric'}
          value={wert ?? ''} min={min} max={max} readOnly={nurLesen} aria-invalid={!!fehler}
          onChange={(e) => onWert(e.target.value)} />
        {einheit && typ !== 'zeit' && <span className="te-einh">{einheit}</span>}
      </div>
      {fehler
        ? <span className="vv-fehl" role="alert"><Ico n="achtung" />{fehler}</span>
        : tipp ? <span className="vv-tipp">{tipp}</span> : null}
    </div>
  );
}

/* ═══ Gruppenliste ═══ */
export function Gruppenliste({ gruppen, d, ctx, offen, onGruppe, filter, onFilter, sel, onWaehle, onLink, zahlen }) {
  const knopf = (f, n, e) => (
    <button type="button" aria-pressed={filter === f} onClick={() => onFilter(f)}>{n}{e != null && <em>{e}</em>}</button>
  );
  const sichtbar = (id) => {
    const s = status(id, d, ctx);
    if (filter === 'offen') return ['fehlt', 'tot', 'fehler'].includes(s);
    if (filter === 'neu') return ['neu', 'fehler'].includes(s);
    return true;
  };
  return (
    <nav className="te-gruppen" aria-label="Bereiche">
      <div className="te-g-filter"><div className="vv-seg">{knopf('alle', 'Alle')}{knopf('offen', 'Offen', zahlen.fehlt + zahlen.fehler)}{knopf('neu', 'Geändert', zahlen.neu + zahlen.fehler)}</div></div>
      {gruppen.map((g) => {
        const ids = g.items.filter((x) => !x.link).map((x) => x.id);
        const st = ids.map((id) => status(id, d, ctx));
        const za = [['te-zg', st.filter((x) => x === 'ok' || x === 'erbt').length], ['te-zgr', st.filter((x) => x === 'zu').length],
          ['te-zy', st.filter((x) => x === 'fehlt' || x === 'tot').length], ['te-zb', st.filter((x) => x === 'neu').length],
          ['te-zr', st.filter((x) => x === 'fehler').length]].filter(([, n]) => n);
        const auf = offen.has(g.key) || filter !== 'alle';
        let letzte = '';
        const zeilen = [];
        g.items.forEach((it) => {
          if (it.link) {
            zeilen.push(<button key={it.id} type="button" className="te-eintrag te-link" onClick={() => onLink(it.link)}>
              <span className="te-n">{it.name}</span><span className="te-w">im Turnier <Ico n="pfeil" /></span></button>);
            return;
          }
          if (!sichtbar(it.id)) return;
          if (g.key !== 'wertung' && it.unter && it.unter !== letzte) { zeilen.push(<h4 key={`h-${it.unter}`}>{it.unter}</h4>); letzte = it.unter; }
          const s = status(it.id, d, ctx);
          zeilen.push(
            <button key={it.id} type="button" className={`te-eintrag${sel === it.id ? ' te-gewaehlt' : ''}`} aria-current={sel === it.id ? 'true' : undefined} onClick={() => onWaehle(it.id)}>
              <span className={`te-punkt ${PUNKT[s]}`} title={STATUS[s][1]} /><span className="te-n">{it.name}</span><span className="te-w">{kurzWert(it.id, d)}</span>
            </button>);
        });
        return (
          <React.Fragment key={g.key}>
            <button type="button" className="te-g-kopf" aria-expanded={auf} onClick={() => onGruppe(g.key)}>
              <span className="te-pf"><Ico n="rechts" /></span><strong>{g.titel}</strong>
              <span className="te-zaehl">{za.map(([k, n]) => <span key={k} className={k}>{n}</span>)}</span>
            </button>
            {auf && <div className="te-g-unter">{zeilen.length ? zeilen : <div className="te-leer-filter">nichts {filter === 'neu' ? 'geändert' : 'offen'}</div>}</div>}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

/* ═══ Bearbeiten ═══ */
function OhneWirkung({ eintraege }) {
  if (!eintraege.length) return null;
  return (
    <details className="te-ohne">
      <summary><Ico n="info" /> Gespeichert, aber ohne Wirkung<span className="vv-marke vv-gelb">{eintraege.length} {eintraege.length === 1 ? 'Feld' : 'Felder'}</span></summary>
      <div className="te-ohne-in">Diese Felder liest heute kein Programmteil. Sie stehen deshalb nicht als Eingabe da; gespeicherte Werte bleiben erhalten.
        <ul>{eintraege.map((e) => <li key={e.id}>{e.warum}</li>)}</ul>
      </div>
    </details>
  );
}

export function EditorInhalt({ id, d, ctx, vorgabe, fehler, setZp, setZpOption, setWertung, setVorlage, onWaehle, onMatten, verbandWahl }) {
  const neuF = new Set(geaenderteFelder(id, d));
  const fe = (feld, unter = '') => fehler[`${id}|${feld}|${unter}`];

  if (id === BASIS_ID) {
    const zu = !ctx.kannZeitplan;
    const schalter = basisSchalter();
    return (
      <>
        {zu && <Gesperrt text="Die Vorgabe für den Zeitplan gilt für alle Verbände. Ändern kann sie nur der Plattform-Admin; für ein Turnier wählst du oben unter „Gilt für“ das Turnier." />}
        <div className="te-teil"><h3>Grundwerte</h3>
          <div className="te-felder te-2">
            <ZahlFeld fid={`${id}|start_time|`} label="Beginn" typ="zeit" wert={d.zp.werte.start_time} onWert={(v) => setZp('start_time', v)}
              fehler={fe('start_time')} neu={neuF.has('Beginn')} nurLesen={zu}
              tipp={ctx.scope === 'std' ? 'Uhrzeit der ersten Kategorie' : `Vorgabe: ${vorgabe?.start_time || '09:00'}`} />
            <ZahlFeld fid={`${id}|pause_between_categories|`} label="Pause zwischen Kategorien" einheit="Min" min={0} max={30}
              wert={d.zp.werte.pause_between_categories} onWert={(v) => setZp('pause_between_categories', v === '' ? '' : Number(v))}
              fehler={fe('pause_between_categories')} neu={neuF.has('Pause zwischen Kategorien')} nurLesen={zu}
              tipp={`0 bis 30 Min, Vorgabe: ${vorgabe?.pause_between_categories ?? 5}`} />
            {schalter.map((s) => {
              const roh = d.zp.werte.disciplines?._optionen?.[s.feld];
              return s.typ === 'schalter'
                ? <ZahlFeld key={s.feld} fid={`${id}|${s.feld}|`} label={s.label} typ="schalter" wert={!!roh}
                    onWert={(v) => setZpOption(s.feld, v)} neu={neuF.has(s.label)} nurLesen={zu} tipp={s.tipp} />
                : <ZahlFeld key={s.feld} fid={`${id}|${s.feld}|`} label={s.label} einheit={s.einheit} min={s.min} max={s.max}
                    wert={roh ?? s.standard} erbt={roh === undefined} onWert={(v) => setZpOption(s.feld, v === '' ? '' : Number(v))}
                    fehler={fe(s.feld)} neu={neuF.has(s.label)} nurLesen={zu} tipp={s.tipp} />;
            })}
          </div>
        </div>
        <div className="te-teil"><h3>Matten</h3>
          {ctx.scope === 'std'
            ? <div className="vv-hinweis te-grau"><Ico n="matte" /><div><b>Je Turnier in der Mattenverwaltung</b>Anzahl und Namen stehen bei jedem Turnier unter Matten.</div></div>
            : Number(d.zp.matten) > 0
              ? <div className="vv-hinweis te-grau"><Ico n="matte" /><div><b>{d.zp.matten} {Number(d.zp.matten) === 1 ? 'Matte' : 'Matten'} angelegt</b>Geändert wird in der Mattenverwaltung.</div><button type="button" className="vv-k vv-k-klein" onClick={onMatten}>Matten öffnen</button></div>
              : <div className="vv-hinweis"><Ico n="achtung" /><div><b>Keine Matte angelegt</b>Ohne Matte kann der Zeitplan nichts verteilen.</div><button type="button" className="vv-k vv-k-klein" onClick={onMatten}>Matten anlegen</button></div>}
        </div>
        <OhneWirkung eintraege={zusatzFuer('_basis', false)} />
      </>
    );
  }

  if (id.startsWith('z:')) {
    const k = id.slice(2); const z = zeileVon(k);
    if (z.ohneWirkung) {
      return (
        <>
          <div className="vv-hinweis"><Ico n="achtung" /><div><b>Diese Zeile wirkt nicht</b>{z.ohneWirkung}</div></div>
          <p style={{ fontSize: 13.5, color: 'var(--vv-text-2)', margin: 0 }}>Gespeicherte Werte bleiben erhalten. Eingaben erscheinen hier, sobald der Zeitplan die Zeile liest (lib/einstellungenWirkung.js).</p>
        </>
      );
    }
    const zu = !ctx.kannZeitplan;
    const erbt = ctx.scope !== 'std' && d.zp.quelle !== 'turnier';
    const felder = zeitplanFelder(k);
    const unterteilung = zusatzFuer(k, true).find((a) => a.art === 'unterteilung');
    const feldZeile = (unter) => felder.filter((f) => !(unter && f.feld === 'breakAfter')).map((f) => {
      const ww = zpWert(d, vorgabe, k, f.feld, unter);
      const tipp = f.feld === 'anzahlKampfrichter'
        ? (f.tipp || 'gilt für neu angelegte Formen-Kategorien')
        : erbt ? 'wie Vorgabe'
          : ww.her === 'eingebaut' ? 'nicht gespeichert, eingebauter Wert'
            : ww.her === 'grundzeile' ? 'wie Grundzeile'
              : f.typ === 'dauer' ? `${dauerText(Math.round(f.min * 60))} bis ${dauerText(Math.round(f.max * 60))} Min, z. B. 1:30`
                : f.min != null ? `${f.min} bis ${f.max}${f.einheit ? ` ${f.einheit}` : ''}` : '';
      return (
        <ZahlFeld key={`${f.feld}-${unter || ''}`} fid={`${id}|${f.feld}|${unter || ''}`} label={f.label} einheit={f.einheit} min={f.min} max={f.max} typ={f.typ}
          wert={ww.w} onWert={(v) => setZp(['disciplines', k, f.feld, unter], f.typ === 'schalter' || f.typ === 'dauer' ? v : (v === '' ? '' : Number(v)))}
          fehler={fe(f.feld, unter || '')} neu={!unter && neuF.has(f.label)} erbt={erbt || ww.her !== 'gesetzt'} nurLesen={zu} tipp={tipp} />
      );
    });
    const r = rechnungText(k, d.zp.werte.disciplines, ctx.platzDrei);
    // K1, Fullcontact, Continous Fighting ohne eigene Zeile: Alt-Zeile „Kickboxen“ (lib/kampfRegeln.js)
    const alt = !d.zp.werte.disciplines?.[k] && (ALT_ZEILEN[k] || []).find((a) => d.zp.werte.disciplines?.[a]);
    return (
      <>
        {alt && <div className="vv-hinweis te-grau"><Ico n="info" /><div><b>Noch keine eigenen Werte</b>Der Zeitplan rechnet mit der früheren Zeile „{alt}“. Sobald du hier etwas änderst und speicherst, bekommt {z.name} eigene Werte.</div></div>}
        {zu && <Gesperrt text="Die Vorgabe für den Zeitplan gilt für alle Verbände. Ändern kann sie nur der Plattform-Admin; für ein Turnier wählst du oben unter „Gilt für“ das Turnier." />}
        <div className="te-teil"><h3>{z.art === 'person' ? 'Zeit je Auftritt' : z.art === 'begegnung' ? 'Zeit je Begegnung' : 'Rundenstruktur'}</h3>
          <div className={`te-felder${felder.length >= 4 ? ' te-4' : ''}`}>{feldZeile(null)}</div>
          {r && (
            <div className="te-rechnung" data-rechenhilfe={k}><Ico n="rechner" />
              <span>{r.vor} <b>{r.zahl}</b> {r.nach}
                {r.zusatz && r.zusatz.length > 0 && <ul className="te-rechnung-teile">{r.zusatz.map((t) => <li key={t}>{t}</li>)}</ul>}
              </span>
            </div>
          )}
          {unterteilung && unterteilung.unter.map(([u, name]) => (
            <div key={u} className="te-unterzeile"><h4>{name}</h4><div className="te-felder te-4">{feldZeile(u)}</div></div>
          ))}
        </div>
        <OhneWirkung eintraege={zusatzFuer(k, false)} />
      </>
    );
  }

  if (id.startsWith('w:')) {
    const typ = id.slice(2); const row = d.wertung.werte[typ];
    const zu = !ctx.plattform;
    const systeme = [['bracket', 'K.O.-System'], ['pool', 'Punkte']].concat(row.wertungs_system === 'round_robin' ? [['round_robin', 'Jeder gegen jeden']] : []);
    const zusatz = wertungFelder(row).filter((f) => f.typ !== 'system' && f.feld !== 'auto_dq_ab_verwarnung');
    const dq = wertungFelder(row).find((f) => f.feld === 'auto_dq_ab_verwarnung');
    const ohne = ausgeblendetWertung(typ).filter((a) => !(a.nurPunkte && row.wertungs_system === 'bracket'));
    // Formen: Kampfrichter seit 23.10.2026 hier (F10); SV: Übungen weiter im Zeitplan (F8)
    const krZeile = typ === 'Selbstverteidigung' ? typ : null;
    return (
      <>
        {zu && <Gesperrt text="Gilt für alle Verbände. Ändern kann diese Vorgabe nur der Plattform-Admin. Für ein Turnier stellst du die Wertung im Turnier ein (Turnier bearbeiten › Einstellungen)." />}
        <div className="te-teil"><h3>Wertungssystem</h3>
          <div className="vv-seg te-seg" role="group" aria-label="Wertungssystem">
            {systeme.map(([v, l]) => (
              <button key={v} type="button" aria-pressed={row.wertungs_system === v} disabled={zu} onClick={() => setWertung(typ, 'wertungs_system', v)}>
                {neuF.has('Wertungssystem') && row.wertungs_system === v && <span className="te-neu" />}{l}</button>
            ))}
          </div>
          <p className="vv-tipp" style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--vv-text-3)' }}>Entscheidet beim Erzeugen der Brackets, ob ein K.O.-Baum entsteht. Bruchtest bekommt nie einen Baum.</p>
        </div>
        {dq && (
          <div className="te-teil"><h3>Verwarnungen</h3>
            <div className="te-felder te-2">
              <ZahlFeld fid={`${id}|auto_dq_ab_verwarnung|`} label={dq.label} min={1} max={20} wert={row.auto_dq_ab_verwarnung ?? ''}
                onWert={(v) => setWertung(typ, 'auto_dq_ab_verwarnung', v === '' ? '' : Number(v))} fehler={fe('auto_dq_ab_verwarnung')}
                neu={neuF.has(dq.label)} nurLesen={zu} tipp="leer = aus. Ab der 3. Verwarnung gibt es ohnehin Minuspunkte." />
            </div>
          </div>
        )}
        {zusatz.length > 0 && (
          <div className="te-teil"><h3>{typ === 'Formen' ? 'Neue Kategorien' : 'Weitere Felder'}</h3>
            {typ === 'Formen' && <p className="vv-tipp" style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--vv-text-3)' }}>Gilt für neu angelegte Formen-Kategorien aller Turniere, solange ein Turnier unter Einstellungen › Wertung nichts Eigenes hat. Bestehende Kategorien bleiben, wie sie sind. Gestrichen wird ab 5 Kampfrichtern.</p>}
            <div className="te-felder te-4">{zusatz.map((f) => (
              <ZahlFeld key={f.feld} fid={`${id}|${f.feld}|`} label={f.label} min={f.min} max={f.max} typ={f.typ} wert={row[f.feld] ?? ''}
                onWert={(v) => setWertung(typ, f.feld, f.typ === 'text' || v === '' ? v : Number(v))} fehler={fe(f.feld)} neu={neuF.has(f.label)} nurLesen={zu} />
            ))}</div>
          </div>
        )}
        {krZeile && (
          <div className="vv-hinweis vv-hinweis-blau"><Ico n="leute" /><div><b>Übungen: steht im Zeitplan</b>
            Wirksam ist die Zahl unter Zeitplan › {zeileVon(krZeile).name}; sie gilt für neu angelegte Kategorien. Die Zahl hier ({d.wertung.basis[typ]?.anzahl_kampfrichter ?? '–'}) bleibt gespeichert.</div>
            <button type="button" className="vv-k vv-k-klein" onClick={() => onWaehle(`z:${krZeile}`)}>Öffnen</button></div>
        )}
        <OhneWirkung eintraege={ohne} />
      </>
    );
  }

  if (id.startsWith('v:')) {
    const k = id.slice(2);
    return (
      <div className={`te-teil${neuF.size ? ' te-ist-neu' : ''}`}>
        <h3>{VORLAGEN.find((v) => v.k === k)?.name}</h3>
        <div className={neuF.size ? 'te-ist-neu' : ''}>
          <AusschreibungVorlagen gesteuert={{
            werte: d.vorlagen?.werte, verbandName: d.vorlagen?.verband_name, feld: k,
            onFeld: setVorlage, ...(verbandWahl || {}),
          }} />
        </div>
      </div>
    );
  }
  return null;
}

function Gesperrt({ text }) {
  return <div className="vv-hinweis te-grau"><Ico n="schloss" /><div><b>Gilt für alle Verbände</b>{text}</div></div>;
}

export function EditorKopf({ id, d, ctx }) {
  const g = id.startsWith('w:') ? 'Wertung' : id.startsWith('v:') ? 'Ausschreibung' : 'Zeitplan';
  const z = id.startsWith('z:') && id !== BASIS_ID ? zeileVon(id.slice(2)) : null;
  const w = id.startsWith('w:') ? d.wertung?.werte[id.slice(2)] : null;
  const unter = id === BASIS_ID ? 'Grundwerte' : z ? (z.unter || z.gruppe) : w ? (w.wertungs_system === 'bracket' ? 'K.O.-Kategorie' : 'Punkte-Kategorie') : 'Vorlage des Verbands';
  return (
    <div className="te-e-kopf">
      <div><div className="te-pfad">{g}</div><h2>{titelVon(id)}</h2><div className="te-unter">{unter}</div></div>
      <StatusMarke id={id} d={d} ctx={ctx} />
    </div>
  );
}
